<!-- ============ TRIGGER BUTTON ============ -->
<button type="button"
        [class]="buttonClass"
        [disabled]="isDisabled"
        [ngbTooltip]="buttonTooltip"
        container="body"
        (click)="openExportModal()">
    <i [class]="buttonIcon" [class.me-1]="showLabel"></i>
    <span *ngIf="showLabel">{{ buttonLabel }}</span>
</button>


<!-- ============ PREVIEW MODAL ============ -->
<ng-template #exportPreviewModal>

    <div class="modal-header py-2">
        <h6 class="modal-title" id="exportPreviewTitle">
            <i class="bi bi-file-earmark-excel text-success me-1"></i>
            Export to Excel &mdash; Preview
        </h6>
        <button type="button" class="btn-close" aria-label="Close"
                [disabled]="isBuilding"
                (click)="closeModal()"></button>
    </div>

    <div class="modal-body pt-2 pb-2">

        <!-- ---- scope picker ---- -->
        <div class="d-flex align-items-center flex-wrap gap-3 mb-2 export-scope-bar">

            <span class="export-scope-label">Rows to export</span>

            <div class="form-check form-check-inline mb-0">
                <input class="form-check-input" type="radio" name="exportScope" id="exportScopePage"
                       [checked]="scope === 'page'"
                       [disabled]="isFetching || isBuilding"
                       (change)="onScopeChange('page')">
                <label class="form-check-label" for="exportScopePage">
                    This page
                    <span class="text-muted">({{ pageRowCount }} rows)</span>
                </label>
            </div>

            <div class="form-check form-check-inline mb-0"
                 [ngbTooltip]="canExportAll ? '' : 'This screen does not support exporting all records'"
                 container="body">
                <input class="form-check-input" type="radio" name="exportScope" id="exportScopeAll"
                       [checked]="scope === 'all'"
                       [disabled]="!canExportAll || isFetching || isBuilding"
                       (change)="onScopeChange('all')">
                <label class="form-check-label" for="exportScopeAll"
                       [class.text-muted]="!canExportAll">
                    All records
                    <span class="text-muted">({{ totalRowCount }} rows)</span>
                </label>
            </div>

            <div class="ms-auto d-flex align-items-center gap-2">
                <span class="badge bg-light text-dark border">
                    {{ columns.length }} column{{ columns.length === 1 ? '' : 's' }}
                </span>
                <span class="badge bg-success-subtle text-success border border-success-subtle"
                      *ngIf="!isFetching">
                    {{ resolvedRowCount }} row{{ resolvedRowCount === 1 ? '' : 's' }} will be exported
                </span>
            </div>
        </div>

        <!-- ---- date range filter (only when the grid shows a date column) ---- -->
        <div class="d-flex align-items-center flex-wrap gap-2 mb-2 export-date-bar" *ngIf="hasDateColumns">

            <span class="export-scope-label">Date filter</span>

            <!-- Plain value/change binding rather than [(ngModel)]: with ngModel on the same
                 element a (change) handler can run before the model is written, and on a date
                 input ngModelChange also fires for partial values. -->
            <select class="form-select form-select-sm export-date-col"
                    [value]="dateFilterKey"
                    [disabled]="isFetching || isBuilding"
                    (change)="onDateColumnChange($event)">
                <option *ngFor="let col of dateColumns" [value]="col.key">{{ col.header }}</option>
            </select>

            <label class="export-date-label" for="exportDateFrom">From</label>
            <input type="date" class="form-control form-control-sm export-date-input" id="exportDateFrom"
                   [value]="dateFrom"
                   [attr.max]="dateTo || null"
                   [disabled]="isFetching || isBuilding"
                   (change)="onDateBoundChange('from', $event)">

            <label class="export-date-label" for="exportDateTo">To</label>
            <input type="date" class="form-control form-control-sm export-date-input" id="exportDateTo"
                   [value]="dateTo"
                   [attr.min]="dateFrom || null"
                   [disabled]="isFetching || isBuilding"
                   (change)="onDateBoundChange('to', $event)">

            <button type="button" class="btn btn-sm btn-outline-secondary export-date-clear"
                    [disabled]="(!dateFrom && !dateTo) || isFetching || isBuilding"
                    (click)="clearDateFilter()">
                <i class="bi bi-x-lg me-1"></i>Clear
            </button>

            <span class="text-danger small ms-1" *ngIf="dateFilterError">
                <i class="bi bi-exclamation-circle me-1"></i>{{ dateFilterError }}
            </span>

            <span class="text-muted small ms-1"
                  *ngIf="!dateFilterError && isDateFilterActive && dateFilteredOutCount > 0 && !isFetching">
                {{ dateFilteredOutCount }} row{{ dateFilteredOutCount === 1 ? '' : 's' }} excluded by this range
            </span>
        </div>

        <!-- ---- messages ---- -->
        <div class="alert alert-warning py-1 px-2 mb-2 small" *ngIf="errorMessage">
            <i class="bi bi-exclamation-triangle me-1"></i>{{ errorMessage }}
        </div>

        <div class="alert alert-warning py-1 px-2 mb-2 small" *ngIf="isCapped">
            <i class="bi bi-exclamation-triangle me-1"></i>
            This export hit the {{ maxExportRows }} row ceiling, so later rows are not included.
            <ng-container *ngIf="hasDateColumns">Narrow the date range to export a complete slice.</ng-container>
        </div>

        <!-- ---- loading ---- -->
        <div class="export-loading" *ngIf="isFetching">
            <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
            <span class="text-muted small">Loading all records&hellip;</span>
        </div>

        <!-- ---- preview grid ---- -->
        <ng-container *ngIf="!isFetching">

            <div class="export-preview-wrapper" *ngIf="columns.length && previewRows.length">
                <table class="table table-sm table-bordered mb-0 export-preview-table">
                    <thead>
                        <tr>
                            <th class="export-serial-col" *ngIf="includeSerial">#</th>
                            <th *ngFor="let col of columns" [title]="col.header">{{ col.header }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let row of previewRows; let i = index">
                            <td class="export-serial-col" *ngIf="includeSerial">{{ i + 1 }}</td>
                            <td *ngFor="let col of columns"
                                [class.text-end]="col.isNumeric"
                                [class.text-center]="col.isDate">
                                {{ displayValue(row, col) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-muted small mt-2" *ngIf="columns.length && previewRows.length">
                <i class="bi bi-info-circle me-1"></i>
                <ng-container *ngIf="isPreviewTruncated; else fullPreviewNote">
                    Previewing the first {{ previewRows.length }} of {{ resolvedRowCount }} rows &mdash;
                    all {{ resolvedRowCount }} rows will be written to the file.
                </ng-container>
                <ng-template #fullPreviewNote>
                    This is the complete set of data that will be written to the file.
                </ng-template>
            </div>

            <div class="export-empty text-center text-muted py-4" *ngIf="!columns.length || !previewRows.length">
                <i class="bi bi-inbox d-block mb-2 fs-4"></i>
                <span class="small" *ngIf="isDateFilterActive && sourceRowCount > 0; else noDataAtAll">
                    No rows fall inside this date range.
                </span>
                <ng-template #noDataAtAll>
                    <span class="small">There is no data to export.</span>
                </ng-template>
            </div>

        </ng-container>
    </div>

    <div class="modal-footer py-2">
        <button type="button" class="btn btn-sm btn-secondary"
                [disabled]="isBuilding"
                (click)="closeModal()">Cancel</button>

        <button type="button" class="btn btn-sm btn-success"
                [disabled]="isFetching || isBuilding || !columns.length || !resolvedRowCount"
                (click)="downloadExcel()">
            <span class="spinner-border spinner-border-sm me-1" *ngIf="isBuilding" role="status"></span>
            <i class="bi bi-download me-1" *ngIf="!isBuilding"></i>
            {{ isBuilding ? 'Preparing file…' : 'Download Excel' }}
        </button>
    </div>

</ng-template>
